/**
 * Was ein gescannter Code bedeutet (KONZEPT.md §8).
 *
 * Es gibt genau zwei Faelle, und beide muessen ohne Nachdenken funktionieren:
 * ein Lagerort-QR und ein Artikel-Code. Dazu den dritten, den §8.3 verlangt --
 * den unbekannten, der **nie** in eine Sackgasse fuehren darf.
 *
 * Seit Stufe 3 wird **auf dem Geraet** aufgeloest, aus der lokalen Kopie. Ein
 * Scanner, der an der Einsatzstelle erst den Server fragen muss, ist dort
 * nutzlos. Die Zerlegung des Rohcodes macht `@thw/core-domain` -- dieselbe
 * Funktion, die auch der Server benutzt. Ein Code, den das Handy findet,
 * findet also auch der Server, und umgekehrt.
 */

import {
  formatiereInventarnummer,
  lagerortNachCode,
  nurAlnum,
  nurZiffern,
  suchSchluessel,
} from "@thw/core-domain";
import type { Artikel, Lagerort } from "@thw/contracts";
import { abgleichen } from "../lokal/abgleich.js";
import { bestandLaden, type Bestand } from "../lokal/bestand.js";

export type Aufgeloest =
  | { art: "lagerort"; lagerort: Lagerort }
  | { art: "artikel"; artikel: Artikel }
  | {
      art: "unbekannt";
      code: string;
      /**
       * Der Code in der Schreibweise einer Inventarnummer, falls er danach
       * aussieht. Damit laesst sich nacherfassen, statt ratlos dazustehen.
       */
      vorschlag: string | null;
    };

/**
 * Holt den Code aus einer Lagerort-URL: den Kurzcode, auf alten Etiketten die
 * Kennung (ADR 0017).
 *
 * Im QR steht `https://<host>/l/K7M2Q`. Wer den Code mit der Kamera des
 * Betriebssystems scannt, landet im Browser; wer ihn in der App scannt,
 * kommt hier vorbei -- und soll dasselbe sehen.
 */
export function lagerortCodeAus(code: string): string | null {
  const treffer = /\/l\/([A-Za-z0-9-]+)\/?$/.exec(code.trim());
  return treffer?.[1] ?? null;
}

/**
 * Einen Lagerort ueber das finden, was hinter `/l/` steht: erst den
 * Kurzcode, dann die Kennung -- gross oder klein geschrieben, wie der Server.
 */
export function lagerortNachCodeIn(bestand: Bestand, code: string): Lagerort | null {
  return lagerortNachCode(bestand.lagerorte.values(), code);
}

/**
 * Loest einen Code gegen den lokalen Bestand auf -- rein, ohne Netz.
 *
 * Verglichen wird wie auf dem Server (`artikelSuchenNachCode`): die Ziffern
 * der Inventarnummer und die Geraetenummer ohne Trennzeichen.
 */
export function aufloesenIn(bestand: Bestand, code: string): Aufgeloest {
  const lagerortCode = lagerortCodeAus(code);
  if (lagerortCode !== null) {
    const lagerort = lagerortNachCodeIn(bestand, lagerortCode);
    // Kein Lagerort mit diesem Code: vielleicht ein Etikett von einem
    // anderen System -- weiter als Artikelcode versuchen, statt aufzugeben.
    if (lagerort !== null) return { art: "lagerort", lagerort };
  }

  // Ein Etikett, das nicht die Nummer traegt -- beim Anlegen nach einem Scan
  // gespeichert, genau so, wie es gelesen wurde (ADR 0026).
  const roh = code.trim();
  if (roh !== "") {
    for (const a of bestand.artikel.values()) {
      if (a.qr_inhalt === roh) return { art: "artikel", artikel: a };
    }
  }

  const { ziffern, alnum } = suchSchluessel(code);
  if (ziffern.length > 0 || alnum.length > 0) {
    let perGeraetenummer: Artikel | null = null;
    for (const a of bestand.artikel.values()) {
      if (ziffern.includes(nurZiffern(a.inventarnummer))) return { art: "artikel", artikel: a };
      if (
        perGeraetenummer === null &&
        a.geraetenummer !== null &&
        alnum.includes(nurAlnum(a.geraetenummer))
      ) {
        perGeraetenummer = a;
      }
    }
    if (perGeraetenummer !== null) return { art: "artikel", artikel: perGeraetenummer };
  }

  return { art: "unbekannt", code, vorschlag: formatiereInventarnummer(code) };
}

/** So lange darf ein Nachfragen beim Server einen Scan hoechstens aufhalten. */
const NACHFRAGE_MS = 4000;

/**
 * Loest einen gescannten Code auf.
 *
 * Erst lokal. Ist der Code dort unbekannt und das Geraet online, kann der
 * Artikel seit dem letzten Abgleich angelegt worden sein: Dann wird der
 * Stammstand nachgeholt -- aber hoechstens ein paar Sekunden lang. Danach
 * gilt, was lokal da ist, und ein unbekannter Code bekommt seinen Ausweg.
 */
export async function codeAufloesen(code: string): Promise<Aufgeloest> {
  const ergebnis = aufloesenIn(await bestandLaden(), code);
  if (ergebnis.art !== "unbekannt" || !globalThis.navigator.onLine) return ergebnis;

  await Promise.race([
    abgleichen({ stammdaten: true }),
    new Promise((fertig) => setTimeout(fertig, NACHFRAGE_MS)),
  ]);
  return aufloesenIn(await bestandLaden(), code);
}
